import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useCallback, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import { RMSTextField, RMSSelect, RMSCheckbox } from '../../../shared/components/form-controls';
import SearchAddressAutoComplete from '../../../shared/components/modules/SearchAddressAutoComplete';
import { useAddressDialog } from 'features/user/hooks/useAddressDialog';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import { SectionCard } from 'features/shared/components/cards';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { createPlace, getPlaceDetails, updatePlaceDetails } from '../../services/placeService';
import addPlaceSchema from '../../schema/addPlaceSchema';
import { buildPlacePhoneFields } from '../../utility/placeContactHelper';
import { getDictionarySelectProps } from 'features/shared/utility/dictionarySelectHelper';
import { DEFAULT_VALUES } from '../../constants/addPlaceFormConstants';

const PlaceDetailsForm = forwardRef(
  ({ ptsPlaceID: placeId, initialAddressId = null, onStateChange, onSaved }, ref) => {
    const [record, setRecord] = useState(null);
    const dispatch = useDispatch();

    const isEditMode = Boolean(placeId);
    const placePerms = usePermissions(PERMISSION_RESOURCES.PLACE);
    const canSavePlace = isEditMode ? placePerms.Edit : placePerms.Create;

    const [submitting, setSubmitting] = useState(false);
    const [loadingPlace, setLoadingPlace] = useState(false);
    const { openAddressDialog } = useAddressDialog();

    const methods = useForm({
      resolver: CreateFormResolver(addPlaceSchema),
      mode: 'onBlur',
      defaultValues: { ...DEFAULT_VALUES, ptsAddressID: initialAddressId },
      disabled: !canSavePlace,
    });

    const { control, setValue } = methods;
    const { isDirty } = useFormState({ control });
    const [ptsAddressID, ContactMethod] = useWatch({
      control,
      name: ['ptsAddressID', 'ContactMethod'],
    });

    const {
      placeTypeOptions,
      contactMethodOptions,
      contactTypeOptions,
    } = useUserDictionaryOptions();

    const loadPlaceDetails = useCallback(() => {
      if (!placeId) return;

      executeApiAction({
        setLoading: setLoadingPlace,
        dispatch,
        action: () => getPlaceDetails(placeId),
        onSuccess: (place) => {
          if (!place) return;
          setRecord(place);
          methods.reset({
            ...DEFAULT_VALUES,
            PlaceName: place.PlaceName || '',
            PlaceORI: place.PlaceORI || '',
            PlaceDepartmentID: place.PlaceDepartmentID || '',
            PlaceType: place.PlaceType || '',
            IsDestination: Boolean(place.IsDestination),
          });
        },
      });
    }, [placeId, dispatch, methods]);

    const handleContactMethodChange = useCallback(() => {
      setValue('ContactMethodType', '');
      setValue('Info', '');
    }, [setValue]);

    const handleAddressCreated = useCallback(
      (address) => {
        setValue('ptsAddressID', address?.ptsAddressID ?? null, {
          shouldDirty: true,
          shouldValidate: true,
        });
      },
      [setValue]
    );

    const onSubmit = async (data) => {
      if (!canSavePlace || submitting || loadingPlace) return;
      if (isEditMode) {
        await executeApiAction({
          setLoading: setSubmitting,
          dispatch,
          action: () =>
            updatePlaceDetails(placeId, {
              PlaceName: data.PlaceName,
              PlaceORI: data.PlaceORI || null,
              PlaceDepartmentID: data.PlaceDepartmentID || null,
              PlaceType: data.PlaceType || null,
              IsDestination: data.IsDestination || false,
            }),
          onSuccess: (updatedPlace) => {
            dispatch(notify('Place updated successfully', 'success'));
            methods.reset(data);
            const saved = {
              ...record,
              ...data,
              ...(updatedPlace && typeof updatedPlace === 'object' ? updatedPlace : {}),
              ptsPlaceID: placeId,
            };
            setRecord(saved);
            onSaved?.(saved);
          },
        });
        return;
      }

      const payload = {
        PlaceName: data.PlaceName,
        PlaceORI: data.PlaceORI || null,
        PlaceDepartmentID: data.PlaceDepartmentID || null,
        PlaceType: data.PlaceType || null,
        IsDestination: data.IsDestination || false,
        IsPrimary: data.IsPrimary || false,
        ptsAddressID: data.ptsAddressID || null,
        ContactMethod: data.ContactMethod || null,
        ContactMethodType: data.ContactMethodType || null,
        Info: data.Info || null,
        Notes: data.Notes || null,
        IsDefault: data.IsDefault || false,
        ...buildPlacePhoneFields(data.ContactMethod, data.Info),
      };

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () => createPlace(payload),
        onSuccess: (place) => {
          dispatch(notify('Place created successfully', 'success'));
          methods.reset(data);
          const saved =
            typeof place === 'object' && place !== null
              ? { ...data, ...place }
              : { ...data, ptsPlaceID: place };
          setRecord(saved);
          onSaved?.(saved);
        },
      });
    };

    const handleSave = methods.handleSubmit(onSubmit);

    useImperativeHandle(ref, () => ({ save: handleSave, isDirty: () => isDirty }), [
      handleSave,
      isDirty,
    ]);

    useEffect(() => {
      loadPlaceDetails();
    }, [loadPlaceDetails]);

    useEffect(() => {
      onStateChange?.({
        isDirty,
        submitting,
        loading: loadingPlace,
        canSave: canSavePlace,
        recordHistory: record,
      });
    }, [isDirty, submitting, loadingPlace, canSavePlace, record, onStateChange]);

    return (
      <FormProvider {...methods}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={isEditMode ? 12 : 6}>
            <div className="pd-column">
              <SectionCard label="Place Details">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <RMSTextField
                      name="PlaceName"
                      label="Place Name"
                      inputProps={{ maxLength: 100 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <RMSSelect
                      name="PlaceType"
                      label="Place Type"
                      options={placeTypeOptions}
                      {...getDictionarySelectProps()}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <RMSTextField
                      name="PlaceORI"
                      label="Place ORI"
                      inputProps={{ maxLength: 20 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <RMSTextField
                      name="PlaceDepartmentID"
                      label="Place Department ID"
                      inputProps={{ maxLength: 20 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <RMSCheckbox name="IsDestination" label="Destination" />
                  </Grid>
                </Grid>
              </SectionCard>
            </div>
          </Grid>

          {!isEditMode && (
            <Grid item xs={12} md={6}>
              <div className="pd-column">
                <SectionCard label="Contact">
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                      <SearchAddressAutoComplete
                        name="ptsAddressID"
                        label="Address Lookup"
                        defaultAddressId={ptsAddressID}
                        onAddIconClick={() =>
                          openAddressDialog(null, {
                            closeOnSave: true,
                            onAddressCreated: handleAddressCreated,
                          })
                        }
                        showDetailsIcon={false}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <RMSCheckbox name="IsPrimary" label="Primary" disabled={!ptsAddressID} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <RMSSelect
                        name="ContactMethod"
                        label="Contact Method"
                        options={contactMethodOptions}
                        onChange={handleContactMethodChange}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <RMSSelect
                        name="ContactMethodType"
                        label="Contact Type"
                        options={contactTypeOptions}
                        disabled={!ContactMethod}
                        {...getDictionarySelectProps()}
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <RMSTextField
                        name="Info"
                        label="Info"
                        disabled={!ContactMethod}
                        inputProps={{ maxLength: 100 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <RMSCheckbox name="IsDefault" label="Default" disabled={!ContactMethod} />
                    </Grid>
                    <Grid item xs={12}>
                      <RMSTextField
                        name="Notes"
                        label="Notes"
                        multiline
                        rows={3}
                        inputProps={{ maxLength: 2000 }}
                      />
                    </Grid>
                  </Grid>
                </SectionCard>
              </div>
            </Grid>
          )}
        </Grid>
      </FormProvider>
    );
  }
);

export default PlaceDetailsForm;
